import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { incidentDisplayName } from "@/api/models";
import type { SopRuleResult, SopRuleResults } from "@/api/queries/sop-compliance";
import { trackEvent, AnalyticsEvent } from "@/analytics";

/**
 * "Your department's SOPs" — the approved-rules results from
 * `/v1/sop-compliance/rule-results` (RES-58/59). Only HUMAN-APPROVED rules
 * appear here (the backend's single choke point guarantees it). Rendering is
 * honest per category:
 *   A (instrumented)  — pass/fail with real evidence links,
 *   B (procedural)    — checklist rows with counts (or "insufficient data"),
 *   C (declared)      — "declared, not instrumented", never scored or gray-failed.
 */

const plural = (n: number) => (n === 1 ? "" : "s");

function StatusBadge({ result }: { result: SopRuleResult }) {
  switch (result.status) {
    case "met":
      return <Badge variant="success">No exceedances</Badge>;
    case "breached":
      return (
        <Badge variant="destructive">
          {result.breach_count} exceedance{plural(result.breach_count)}
        </Badge>
      );
    case "insufficient_data":
      return <Badge variant="outline">Insufficient data</Badge>;
    case "not_instrumented":
      return <Badge variant="outline">Declared, not instrumented</Badge>;
  }
}

function EvidenceList({ result }: { result: SopRuleResult }) {
  if (result.evidence.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
      {result.evidence.map((ev, i) => (
        <li key={`${ev.incident_id}-${i}`}>
          <Link
            to={`/incidents/${ev.incident_id}`}
            className="text-primary hover:underline"
            // No id prop: raw incident UUIDs never reach analytics.
            onClick={() => trackEvent(AnalyticsEvent.SopIncidentOpened)}
          >
            {incidentDisplayName({
              id: ev.incident_id,
              incident_type: ev.incident_type,
              address: ev.address,
              incident_number: ev.incident_number,
            })}
          </Link>
          {": "}
          {ev.display_name ? `${ev.display_name}: ` : ""}
          {ev.detail}
        </li>
      ))}
    </ul>
  );
}

function InstrumentedRule({ result }: { result: SopRuleResult }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{result.rule_code}</span>
        {result.source_citation ? (
          <span className="text-xs text-muted-foreground">
            {result.source_citation}
          </span>
        ) : null}
        <StatusBadge result={result} />
      </div>
      <blockquote className="mt-2 border-l-2 border-border pl-3 text-sm italic text-muted-foreground">
        &ldquo;{result.rule_text}&rdquo;
      </blockquote>
      <p className="mt-1 text-xs text-muted-foreground">
        {result.evaluated_records} record{plural(result.evaluated_records)} evaluated
        {result.confidence ? ` · confidence: ${result.confidence}` : ""}
      </p>
      <EvidenceList result={result} />
    </div>
  );
}

function ProceduralRule({ result }: { result: SopRuleResult }) {
  const ok = result.status === "met";
  const marker = ok ? "●" : result.status === "breached" ? "●" : "○";
  const markerClass = ok
    ? "text-success"
    : result.status === "breached"
      ? "text-destructive"
      : "text-muted-foreground/50";
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex gap-2">
        <span className={markerClass}>{marker}</span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{result.rule_code}</span>
            {result.source_citation ? (
              <span className="text-xs text-muted-foreground">
                {result.source_citation}
              </span>
            ) : null}
            <StatusBadge result={result} />
          </div>
          <blockquote className="mt-1 text-sm italic text-muted-foreground">
            &ldquo;{result.rule_text}&rdquo;
          </blockquote>
          {result.status === "insufficient_data" ? (
            <p className="mt-1 text-xs text-muted-foreground">
              {`RespAI cannot check this yet${
                result.not_available.length > 0
                  ? `. Missing: ${result.not_available
                      .map((k) => k.replace(/_/g, " "))
                      .join(", ")}`
                  : ""
              }.`}
            </p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">
              {result.evaluated_records} record{plural(result.evaluated_records)}{" "}
              evaluated · {result.breach_count}{" "}
              {result.breach_count === 1 ? "breach" : "breaches"}
            </p>
          )}
          <EvidenceList result={result} />
        </div>
      </div>
    </div>
  );
}

function DeclaredRule({ result }: { result: SopRuleResult }) {
  return (
    <li className="flex gap-2 text-sm text-muted-foreground">
      <span className="text-muted-foreground/50">○</span>
      <span>
        <span className="italic">&ldquo;{result.rule_text}&rdquo;</span>
        {result.source_citation ? ` (${result.source_citation})` : ""}
      </span>
    </li>
  );
}

export function DepartmentSopRules({ data }: { data: SopRuleResults }) {
  const instrumented = data.results.filter((r) => r.category === "A");
  const procedural = data.results.filter((r) => r.category === "B");
  const declared = data.results.filter((r) => r.category === "C");

  if (data.results.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Your department&apos;s SOPs</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No approved SOP rules yet. Upload your SOP under SOP Documents,
            extract its rules, and approve the ones that should score. Until you
            do, the recommendations above stay illustrative.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>Your department&apos;s SOPs</CardTitle>
          <Badge variant="secondary">
            {data.results.length} approved rule{plural(data.results.length)}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Rules quoted verbatim from your uploaded SOPs, approved in review, and
          scored against real exposure records (limits {data.limits_version}).
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {instrumented.length > 0 ? (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Instrumented (scored)</h3>
            {instrumented.map((r) => (
              <InstrumentedRule key={r.rule_id} result={r} />
            ))}
          </div>
        ) : null}
        {procedural.length > 0 ? (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Procedural (checked)</h3>
            {procedural.map((r) => (
              <ProceduralRule key={r.rule_id} result={r} />
            ))}
          </div>
        ) : null}
        {declared.length > 0 ? (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground">
              Declared, not instrumented
            </h3>
            <ul className="space-y-1.5">
              {declared.map((r) => (
                <DeclaredRule key={r.rule_id} result={r} />
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">
              Your SOP declares these and we cannot measure them yet. They are
              listed so the report never overstates what we checked.
            </p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
